import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Heart, Scroll, LockSimple } from "@phosphor-icons/react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useFavourites } from "@/contexts/FavouritesContext";
import RecipeCard from "@/components/RecipeCard";
import HeartButton from "@/components/HeartButton";
import PageMeta from "@/components/PageMeta";
import { recipePath } from "@/lib/slug";
import { matchFavourites, favouritePath } from "@/lib/favourites";

function UnavailableCard({ it, onRemove }) {
  return (
    <div className="card-recipe !p-6 flex flex-col gap-3 bg-rf-cream-alt" data-testid={`fav-unavailable-${it.item_id}`}>
      <p className="text-[11px] font-black uppercase tracking-widest text-rf-ink-soft">No longer available</p>
      <h3 className="font-display font-extrabold text-xl text-rf-ink line-clamp-2">{it.title}</h3>
      <p className="text-sm text-rf-ink-soft">This recipe was deleted by its owner or is no longer shared, so we can&apos;t show it. You can keep it here or remove it.</p>
      <button type="button" onClick={onRemove} data-testid={`fav-remove-${it.item_id}`} className="btn-outline-ink !py-2 !px-4 text-sm self-start">Remove</button>
    </div>
  );
}

function HistoricalFavCard({ it }) {
  const r = it.recipe;
  return (
    <div className="relative">
      <Link to={favouritePath(it, recipePath)} data-testid={`fav-historical-${it.item_id}`} className="card-recipe hover-lift block !p-0 overflow-hidden h-full">
        <div className="aspect-[4/3] bg-gradient-to-br from-amber-800 to-rf-ink relative">
          {r.image_url ? <img src={r.image_url} alt={r.title} loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
            : <Scroll size={72} weight="fill" className="absolute inset-0 m-auto text-rf-cream/20" />}
        </div>
        <div className="p-5">
          <p className="text-[10px] uppercase tracking-widest text-rf-ink-soft mb-1">Historical · {r.year > 0 ? r.year : `${Math.abs(r.year)} BC`}</p>
          <h3 className="font-display font-extrabold text-xl text-rf-ink line-clamp-2">{r.title}</h3>
        </div>
      </Link>
      <HeartButton kind="historical" id={it.item_id} count={r.favourite_count} className="absolute top-3 right-3 z-10" />
    </div>
  );
}

export default function Favourites() {
  const { user, loading } = useAuth();
  const fav = useFavourites();
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState(null);
  const q = params.get("q") || "";

  const load = () => api.get("/favourites").then(({ data }) => setItems(data.items)).catch(() => setItems([]));
  useEffect(() => { if (!loading && !user) nav("/login?next=%2Ffavourites"); else if (user) load(); }, [user, loading]); // eslint-disable-line react-hooks/exhaustive-deps

  const remove = async (it) => { if (await fav.toggle(it.kind, it.item_id)) load(); };
  const visible = (items || []).filter((it) => it.available ? fav.isSaved(it.kind, it.item_id) : true);
  const shown = matchFavourites(visible, q);

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-14" data-testid="favourites-page">
      <PageMeta title="My Favourites — Recipe Filer" noindex />
      <Link to="/dashboard" className="text-sm font-bold text-rf-orange hover:underline">← My Cookbooks</Link>
      <div className="flex flex-wrap items-end justify-between gap-4 mt-4 mb-8">
        <div className="flex flex-col">
          <p className="overline mb-2 inline-flex items-center gap-1 self-start"><LockSimple size={12} weight="bold" /> Private · only you can see this</p>
          <h1 className="font-display text-4xl sm:text-5xl font-black text-rf-ink inline-flex items-center gap-3">
            <Heart size={40} weight="fill" className="text-rose-600" /> Favourites
          </h1>
          <p className="text-rf-ink-soft mt-2" data-testid="favourites-count">{visible.length} saved {visible.length === 1 ? "recipe" : "recipes"}{q ? ` · ${shown.length} match "${q}"` : ""}</p>
        </div>
        <input value={q} onChange={(e) => setParams(e.target.value ? { q: e.target.value } : {}, { replace: true })}
               placeholder="Search your favourites" data-testid="favourites-search" className="input-rf max-w-xs" />
      </div>
      {items === null ? <p className="text-rf-ink-soft">Loading…</p> : shown.length === 0 ? (
        <div className="card-recipe text-center py-16" data-testid="favourites-empty">
          <Heart size={40} className="mx-auto text-rose-400 mb-3" />
          <p className="text-rf-ink-soft">{q ? "No favourites match that search." : "Tap the heart on any recipe to save it here."}</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="favourites-grid">
          {shown.map((it) => !it.available ? <UnavailableCard key={`${it.kind}${it.item_id}`} it={it} onRemove={() => remove(it)} />
            : it.kind === "historical" ? <HistoricalFavCard key={`h${it.item_id}`} it={it} />
              : <RecipeCard key={it.item_id} recipe={it.recipe} />)}
        </div>
      )}
    </div>
  );
}
